import * as Sentry from '@sentry/browser';
import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GetCurrentUser } from '@resources/current-user.graphql';
import { GetUserRights } from '@resources/rights.graphql';
import InlineLoadingIndicator from '@common/components/loading-indicator/inline-loading-indicator';
import { ErrorComponent } from '@common/components/error-component/error-component';

const SessionContext = createContext(null);

export const useSession = () => {
  return useContext(SessionContext);
};

export const logOut = () => {
  window.location = '/eduphoria_webcontrols/Logout.aspx';
};

const SessionProvider = ({ children, app, includePermissions }) => {
  const {
    data: userData,
    loading: userLoading,
    error: userError
  } = useQuery(GetCurrentUser);

  const {
    data: rightsData,
    loading: rightsLoading,
    error: rightsError
  } = useQuery(GetUserRights, {
    variables: { app, includePermissions }
  });

  const currentUser = userData?.currentUser;
  const {
    rights,
    permissions,
    customProperties,
    teacherProperties = {},
    licensedApplications
  } = rightsData?.userRights || {};

  const currentUserData = useMemo(
    () => ({
      currentUser,
      rights,
      permissions,
      customProperties,
      teacherProperties,
      licensedApplications
    }),
    [
      currentUser,
      rights,
      permissions,
      customProperties,
      teacherProperties,
      licensedApplications
    ]
  );

  useEffect(() => {
    if (currentUser) {
      Sentry.configureScope(scope => {
        const { organizationId, userId, username, customerId } = currentUser;
        scope.setUser({ organizationId, userId, username, customerId });
      });
    }
  }, [currentUser]);

  if (
    userError?.networkError?.statusCode === 401 ||
    (currentUser && !currentUser.isAuthenticated)
  ) {
    logOut();
  }

  if (userError || rightsError) {
    return (
      <ErrorComponent
        errorMessage={{
          data:
            userError?.networkError?.result ||
            userError ||
            rightsError?.networkError?.result ||
            rightsError
        }}
      />
    );
  }

  return (
    <SessionContext.Provider value={currentUserData}>
      {userLoading || rightsLoading ? <InlineLoadingIndicator /> : children}
    </SessionContext.Provider>
  );
};

export default SessionProvider;
